'use client';

import Link from 'next/link';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import type { z } from 'zod';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@kit/ui/input-group';
import { Trans } from '@kit/ui/trans';

import { PasswordSignInSchema } from '../schemas/password-sign-in.schema';
import { PasswordInput } from './password-input';

export function PasswordSignInForm({
  onSubmit,
  captchaLoading = false,
  loading = false,
  redirecting = false,
  captchaField,
  prefilledEmail,
  onChangeEmail,
}: {
  onSubmit: (params: z.infer<typeof PasswordSignInSchema>) => unknown;
  captchaLoading: boolean;
  loading: boolean;
  redirecting: boolean;
  captchaField?: ReactNode;
  prefilledEmail?: string;
  onChangeEmail?: () => void;
}) {
  const { t } = useTranslation('auth');

  const form = useForm({
    resolver: zodResolver(PasswordSignInSchema),
    defaultValues: {
      email: prefilledEmail ?? '',
      password: '',
    },
  });

  return (
    <Form {...form}>
      <form
        className={'flex w-full flex-col gap-y-4'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className={'flex flex-col space-y-2.5'}>
          <FormField
            control={form.control}
            name={'email'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputGroup className="dark:bg-background">
                    <InputGroupAddon>
                      <Mail className="h-4 w-4" />
                    </InputGroupAddon>

                    <InputGroupInput
                      data-test={'email-input'}
                      required
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      readOnly={Boolean(prefilledEmail)}
                      {...field}
                    />
                  </InputGroup>
                </FormControl>

                <FormMessage />

                {prefilledEmail && onChangeEmail && (
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      variant="link"
                      className="text-xs"
                      onClick={onChangeEmail}
                    >
                      <Trans i18nKey="auth:useDifferentEmail" />
                    </Button>
                  </div>
                )}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'password'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PasswordInput {...field} />
                </FormControl>

                <FormMessage />

                <div>
                  <Button
                    asChild
                    type={'button'}
                    size={'sm'}
                    variant={'link'}
                    className={'text-xs'}
                  >
                    <Link href={'/auth/password-reset'}>
                      <Trans i18nKey={'auth:passwordForgottenQuestion'} />
                    </Link>
                  </Button>
                </div>
              </FormItem>
            )}
          />
        </div>

        <div className={'flex justify-center'}>
          {captchaField}
        </div>

        <Button
          data-test="auth-submit-button"
          className={'group w-full'}
          type="submit"
          disabled={loading || redirecting || captchaLoading}
        >
          <If condition={redirecting}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:redirecting'} />
            </span>
          </If>

          <If condition={loading}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:signingIn'} />
            </span>
          </If>

          <If condition={captchaLoading}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:verifyingCaptcha'} />
            </span>
          </If>

          <If condition={!redirecting && !loading && !captchaLoading}>
            <span
              className={
                'animate-in fade-in slide-in-from-bottom-24 flex items-center'
              }
            >
              <Trans i18nKey={'auth:signInWithEmail'} />

              <ArrowRight
                className={
                  'zoom-in animate-in slide-in-from-left-2 fill-mode-both h-4 delay-500 duration-500'
                }
              />
            </span>
          </If>
        </Button>
      </form>
    </Form>
  );
}
